import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, FileSpreadsheet, Download, TrendingUp, DollarSign,
  AlertTriangle, CheckCircle, Target, Zap, ChevronRight, Pill,
  Lightbulb, Shield, Loader2, Sparkles, ArrowUpRight,
  Brain, Siren, ToggleLeft, ToggleRight,
  ChevronDown, ChevronUp, RefreshCw, ArrowRight, Rocket,
  ShieldAlert, Send, Eye, EyeOff, Info
} from 'lucide-react';
import { 
  uploadFile, downloadSampleTemplate, getUploads, getUploadAnalysis, 
  getDashboardMetrics, getFeaturedDrugs, getAnalystBrief, getWasteAlerts,
  dismissIntervention, actOnIntervention, getDailyLoss
} from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ─── Format currency smartly ───────────────────────────────
function fmt$(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

const cBadge = {
  High: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  Medium: 'text-amber-700 bg-amber-50 border-amber-200',
  Low: 'text-red-700 bg-red-50 border-red-200',
};

// ─── Upload Zone ───────────────────────────────────────────
function UploadZone({ onUpload, uploading, compact }) {
  const [dragOver, setDragOver] = useState(false);
  const ref = React.useRef(null);
  const drop = useCallback((e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) onUpload(e.dataTransfer.files[0]); }, [onUpload]);
  if (compact) return (
    <div onClick={() => !uploading && ref.current?.click()} className="border border-dashed border-slate-300 rounded-xl p-3.5 text-center cursor-pointer hover:border-[#0A2540]/50 hover:bg-slate-50/50 transition-all" data-testid="upload-zone">
      <input ref={ref} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} className="hidden" data-testid="upload-file-input" />
      {uploading ? <div className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-[#0A2540]" /><span className="text-sm text-slate-600">Analyzing...</span></div>
       : <div className="flex items-center justify-center gap-2"><Upload className="w-4 h-4 text-slate-400" /><span className="text-sm text-slate-600">Upload new data</span><span className="text-xs text-slate-400">.xlsx / .csv</span></div>}
    </div>
  );
  return (
    <div onDrop={drop} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
      onClick={() => !uploading && ref.current?.click()}
      className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-300 ${dragOver ? 'border-[#0A2540] bg-[#0A2540]/5 scale-[1.01]' : uploading ? 'border-slate-200 bg-slate-50 cursor-wait' : 'border-slate-300 hover:border-[#0A2540]/50 hover:bg-slate-50/50'}`}
      data-testid="upload-zone">
      <input ref={ref} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} className="hidden" data-testid="upload-file-input" />
      {uploading ? <div className="flex flex-col items-center gap-3"><Loader2 className="w-12 h-12 text-[#0A2540] animate-spin" /><p className="text-lg font-semibold text-slate-700">Analyzing your data...</p></div>
       : <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 bg-[#0A2540]/10 rounded-2xl flex items-center justify-center"><Upload className="w-8 h-8 text-[#0A2540]" /></div>
          <p className="text-lg font-semibold text-slate-800">Upload your hospital's medication spend data</p>
          <p className="text-sm text-slate-500">Drop your last 6-12 months of procurement data. We'll identify where you're losing money.</p>
          <div className="flex gap-3 mt-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"><FileSpreadsheet className="w-3.5 h-3.5" /> CSV</span>
          </div>
          <div className="mt-3 flex items-start gap-1.5 text-left bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 max-w-lg mx-auto" data-testid="phi-warning">
            <ShieldAlert className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-amber-700 leading-relaxed">
              <strong>Do not upload patient-identifiable or protected health data (PHI).</strong> This platform processes aggregate procurement and spend data only. Ensure data is de-identified before upload.
            </p>
          </div>
        </div>}
    </div>
  );
}

// ─── CARD 1: Where You're Losing Money ─────────────────────
function LosingMoneyCard({ insights, totalLoss }) {
  const top3 = insights.filter(i => i.type === 'savings_opportunity').slice(0, 3);
  const maxVal = top3[0]?.impact_value || 1;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid="card-losing-money">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center"><DollarSign className="w-4 h-4 text-red-600" /></div>
        <h3 className="font-bold text-slate-900">Where You're Losing Money</h3>
      </div>
      <p className="text-3xl font-black text-red-600 mt-3 tracking-tight">{fmt$(totalLoss)}</p>
      <p className="text-xs text-slate-500 mb-4">Recoverable annual spend</p>
      <div className="space-y-2.5">
        {top3.map((item, i) => (
          <div key={item.id || i} className="group">
            <div className="flex items-center justify-between mb-1">
              <Link to={item.drug_id ? `/drug/${item.drug_id}` : '#'} className="text-sm font-medium text-slate-800 hover:text-[#0A2540] transition-colors flex items-center gap-1">
                {item.title?.split(' on ')[1] || item.title?.split('savings on ')[1] || `Opportunity ${i+1}`}
                <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <span className="text-sm font-bold text-slate-900">{fmt$(item.impact_value)}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-gradient-to-r from-red-400 to-red-500 h-2 rounded-full transition-all duration-700" style={{ width: `${(item.impact_value / maxVal) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CARD 2: Safest Place to Start ─────────────────────────
function SafestStartCard({ suggestions, mode }) {
  const top = suggestions.filter(d => d.best_confidence >= 85).slice(0, 2);
  if (top.length === 0 && suggestions.length > 0) top.push(suggestions[0]);
  return (
    <div className="bg-white border-2 border-emerald-200 rounded-2xl p-6 hover:shadow-lg transition-shadow relative overflow-hidden" data-testid="card-safest-start">
      <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">START HERE</div>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-4 h-4 text-emerald-600" /></div>
        <h3 className="font-bold text-slate-900">Safest Place to Start</h3>
      </div>
      <div className="mt-4 space-y-4">
        {top.map((drug, i) => (
          <div key={drug.drug_id} className="group">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-900">{drug.brand_name}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cBadge.High}`}>High Confidence</span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Switch to {drug.best_generic}</p>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3">
                <span className="text-xl font-black text-emerald-600">${drug.potential_savings?.toLocaleString()}</span>
                {drug.savings_percent && <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{(drug.savings_percent * 100).toFixed(0)}% less</span>}
              </div>
              <Link to={`/drug/${drug.drug_id}`} className="px-3 py-1.5 text-xs font-semibold bg-[#0A2540] text-white rounded-lg hover:bg-[#0A2540]/90 transition-colors flex items-center gap-1" data-testid={`start-review-${drug.drug_id}`}>
                <Send className="w-3 h-3" /> Send to Review
              </Link>
            </div>
          </div>
        ))}
        {top.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Upload data to see opportunities</p>}
      </div>
    </div>
  );
}

// ─── CARD 3: Risks to Watch ────────────────────────────────
function RisksCard({ insights, wasteAlerts }) {
  const [expanded, setExpanded] = useState({});
  const risks = [
    ...insights.filter(i => i.type === 'concentration_risk' || i.type === 'spend_alert').slice(0, 2).map(i => ({
      id: i.id, drug: i.category || i.title?.match(/in (.+)$/)?.[1] || 'Portfolio',
      type: i.type === 'concentration_risk' ? 'Concentration' : 'Spend',
      severity: i.confidence === 'High' || (i.impact_value && i.impact_value > 100000) ? 'high' : 'moderate',
      detail: i.description,
    })),
    ...wasteAlerts.slice(0, 2).map(w => ({
      id: w.id, drug: w.drug_name || w.title?.split(':')[0] || 'Alert',
      type: w.type === 'supply_risk' ? 'Supply' : w.type === 'price_anomaly' ? 'Price' : 'Procurement',
      severity: w.urgency === 'high' ? 'high' : 'moderate',
      detail: w.description || w.title,
    })),
  ].slice(0, 4);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid="card-risks">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center"><ShieldAlert className="w-4 h-4 text-amber-600" /></div>
        <h3 className="font-bold text-slate-900">Risks to Watch</h3>
      </div>
      {risks.length > 0 ? (
        <div className="space-y-2">
          {risks.map(r => (
            <div key={r.id} className="group">
              <div className="flex items-center gap-2 py-1.5 cursor-pointer" onClick={() => setExpanded(p => ({...p, [r.id]: !p[r.id]}))}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.severity === 'high' ? 'bg-red-500' : 'bg-amber-400'}`} />
                <span className="text-sm font-medium text-slate-800 flex-1">{r.drug}</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.severity === 'high' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>{r.type}</span>
                {expanded[r.id] ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}
              </div>
              {expanded[r.id] && (
                <div className="ml-4 pb-2 text-xs text-slate-500 border-l-2 border-slate-100 pl-3 animate-in fade-in">
                  {r.detail}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400 text-center py-4">No risks detected</p>
      )}
    </div>
  );
}

// ─── CARD 4: 3 Actions This Month ──────────────────────────
function ActionsThisMonthCard({ suggestions, mode }) {
  const top3 = suggestions.slice(0, 3);
  return (
    <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-6 text-white hover:shadow-lg transition-shadow" data-testid="card-actions-month">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center"><Rocket className="w-4 h-4 text-emerald-400" /></div>
        <h3 className="font-bold">If You Do Only 3 Things This Month</h3>
      </div>
      <div className="space-y-3">
        {top3.map((drug, i) => {
          const conf = drug.best_confidence >= 85 ? 'High' : drug.best_confidence >= 70 ? 'Medium' : 'Low';
          return (
            <div key={drug.drug_id} className="flex items-center gap-3 bg-white/5 rounded-xl p-3 group hover:bg-white/10 transition-colors">
              <div className="w-7 h-7 bg-white/10 rounded-full flex items-center justify-center text-sm font-bold text-emerald-400 flex-shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm truncate">{drug.brand_name}</span>
                  <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-emerald-400 font-bold text-sm">${drug.potential_savings?.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${conf === 'High' ? 'bg-emerald-500/20 text-emerald-300' : conf === 'Medium' ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'}`}>{conf}</span>
                  <span className="text-[10px] text-slate-400">{drug.best_generic}</span>
                </div>
              </div>
              <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <Link to={`/drug/${drug.drug_id}`} className="px-2.5 py-1 text-[10px] font-semibold bg-white/10 rounded-md hover:bg-white/20 transition-colors" data-testid={`review-action-${drug.drug_id}`}>Review</Link>
                <Link to="/pipeline" className="px-2.5 py-1 text-[10px] font-semibold bg-emerald-500 text-white rounded-md hover:bg-emerald-400 transition-colors" data-testid={`approve-action-${drug.drug_id}`}>Approve</Link>
              </div>
            </div>
          );
        })}
        {top3.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Upload data to see your top actions</p>}
      </div>
    </div>
  );
}

// ─── Next Best Action Banner ───────────────────────────────
function NextBestAction({ suggestions, guided }) {
  if (!guided || suggestions.length === 0) return null;
  // Pick the best high-confidence suggestion, not just highest $
  const best = suggestions.find(d => d.best_confidence >= 85) || suggestions[0];
  const conf = best.best_confidence >= 85 ? 'High' : best.best_confidence >= 70 ? 'Medium' : 'Low';
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between" data-testid="next-best-action">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
        <div>
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Start Here</p>
          <p className="text-sm font-bold text-slate-900">
            Switch {best.brand_name} — your fastest {fmt$(best.potential_savings || 0)} win
          </p>
        </div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cBadge[conf]}`}>{conf} confidence</span>
      </div>
      <Link to={`/drug/${best.drug_id}`} className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-1.5" data-testid="start-here-btn">
        Review Now <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

// ─── Waste Detection ───────────────────────────────────────
function WasteAlerts({ guided, alerts, setAlerts }) {
  const handleDismiss = async (id) => {
    try { await dismissIntervention(id); setAlerts(prev => prev.filter(a => a.id !== id)); toast.success('Dismissed'); } catch { toast.error('Failed'); }
  };
  const handleAct = async (id) => {
    try { await actOnIntervention(id); setAlerts(prev => prev.filter(a => a.id !== id)); toast.success('Action recorded'); } catch { toast.error('Failed'); }
  };
  if (alerts.length === 0) return null;
  const urgClr = { high: 'border-l-red-500', medium: 'border-l-amber-500', low: 'border-l-blue-500' };
  return (
    <div data-testid="waste-alerts">
      <div className="flex items-center gap-2 mb-3">
        <Siren className="w-5 h-5 text-red-500" />
        <h2 className="font-bold text-slate-900">Waste Detection</h2>
        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">{alerts.length} active</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {alerts.slice(0, 4).map(a => (
          <div key={a.id} className={`bg-white border border-slate-200 border-l-4 ${urgClr[a.urgency] || urgClr.medium} rounded-xl p-4 hover:shadow-md transition-all`} data-testid={`waste-alert-${a.id}`}>
            <p className="font-semibold text-slate-900 text-sm leading-tight">{a.title}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm font-bold text-red-600">${a.estimated_annual_impact?.toLocaleString()}/yr</span>
              <div className="flex gap-1.5">
                <button onClick={() => handleDismiss(a.id)} className="px-2 py-1 text-[10px] font-medium text-slate-500 bg-slate-100 rounded hover:bg-slate-200" data-testid={`dismiss-waste-${a.id}`}>Dismiss</button>
                <button onClick={() => handleAct(a.id)} className="px-2 py-1 text-[10px] font-medium text-white bg-[#0A2540] rounded hover:bg-[#0A2540]/90" data-testid={`act-waste-${a.id}`}>Act Now</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Snapshot KPIs ─────────────────────────────────────────
function SnapshotRow({ snapshot, savingsEst, mode }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="snapshot-cards">
      {[
        { icon: DollarSign, label: 'Total Spend', value: fmt$(snapshot.total_spend), color: 'bg-slate-700' },
        { icon: Pill, label: 'Unique Drugs', value: snapshot.unique_drugs, color: 'bg-blue-600' },
        { icon: AlertTriangle, label: 'Branded Spend', value: `${snapshot.branded_spend_pct}%`, color: 'bg-amber-500' },
        { icon: CheckCircle, label: mode === 'cfo' ? 'Actionable' : 'With Generics', value: savingsEst?.matched_count || 0, color: 'bg-emerald-600' },
      ].map((k, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${k.color}`}><k.icon className="w-4 h-4 text-white" /></div>
            <span className="text-xs font-medium text-slate-500">{k.label}</span>
          </div>
          <p className="text-xl font-bold text-slate-900">{k.value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Missed Yesterday (Daily Money Loss) ───────────────────
function MissedYesterday({ dailyLoss }) {
  if (!dailyLoss || !dailyLoss.has_data) return null;
  return (
    <div className="bg-gradient-to-r from-red-950 to-red-900 rounded-2xl p-5 text-white relative overflow-hidden" data-testid="missed-yesterday">
      <div className="absolute top-0 right-0 w-40 h-40 bg-red-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center">
          <Siren className="w-4 h-4 text-red-400" />
        </div>
        <h2 className="font-bold text-white text-sm uppercase tracking-wider">Yesterday You Missed</h2>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <p className="text-3xl font-black text-red-400">${dailyLoss.yesterday?.toLocaleString()}</p>
          <p className="text-[10px] text-red-300/70 uppercase tracking-wider mt-1">Yesterday</p>
        </div>
        <div className="text-center border-x border-red-800">
          <p className="text-3xl font-black text-red-300">${dailyLoss.this_week?.toLocaleString()}</p>
          <p className="text-[10px] text-red-300/70 uppercase tracking-wider mt-1">This Week</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-black text-white">${dailyLoss.this_month?.toLocaleString()}</p>
          <p className="text-[10px] text-red-300/70 uppercase tracking-wider mt-1">This Month</p>
        </div>
      </div>
      {dailyLoss.drivers?.length > 0 && (
        <div className="space-y-2 border-t border-red-800 pt-3">
          <p className="text-[10px] text-red-400 uppercase tracking-wider font-semibold">Top Leaking Drugs</p>
          {dailyLoss.drivers.map((d, i) => (
            <div key={i} className="flex items-center justify-between bg-red-500/10 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-red-400 w-5">{i + 1}.</span>
                <Link to={d.drug_id ? `/drug/${d.drug_id}` : '#'} className="text-sm font-semibold text-white hover:text-red-300 transition-colors" data-testid={`missed-drug-${i}`}>
                  {d.drug}
                </Link>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">{d.reason}</span>
              </div>
              <span className="text-sm font-bold text-red-400">${d.daily_loss}/day</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ROI Calculator ────────────────────────────────────────
function RoiCalculator({ analysis }) {
  const [recoveryPct, setRecoveryPct] = useState(50);
  const matched = analysis?.matched_drugs || [];
  const totalAnnualSavings = matched.reduce((s, d) => s + (d.potential_savings || 0), 0);

  if (totalAnnualSavings === 0) return null;

  const projected = totalAnnualSavings * (recoveryPct / 100);
  const monthly = projected / 12;
  const fiveYear = projected * 5;
  const breakpoints = [10, 25, 50, 75, 100];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow" data-testid="roi-calculator">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
        </div>
        <h3 className="font-bold text-slate-900">ROI Calculator</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">Drag to see what you'd recover at different adoption rates</p>

      {/* Slider */}
      <div className="relative mb-5">
        <input
          type="range" min="5" max="100" value={recoveryPct}
          onChange={(e) => setRecoveryPct(Number(e.target.value))}
          className="w-full h-2 bg-slate-100 rounded-full appearance-none cursor-pointer accent-emerald-500 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-md"
          data-testid="roi-slider"
        />
        <div className="flex justify-between mt-1">
          {breakpoints.map(bp => (
            <button key={bp} onClick={() => setRecoveryPct(bp)}
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${recoveryPct === bp ? 'bg-emerald-100 text-emerald-700' : 'text-slate-400 hover:text-slate-600'}`}>
              {bp}%
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-4 mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-emerald-600 font-semibold uppercase tracking-wider">At {recoveryPct}% adoption</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div>
            <p className="text-2xl font-black text-emerald-700">{fmt$(monthly)}</p>
            <p className="text-[10px] text-emerald-600/70">Monthly</p>
          </div>
          <div className="border-x border-emerald-200 text-center">
            <p className="text-2xl font-black text-emerald-700">{fmt$(projected)}</p>
            <p className="text-[10px] text-emerald-600/70">Annual</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black text-emerald-700">{fmt$(fiveYear)}</p>
            <p className="text-[10px] text-emerald-600/70">5-Year</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-slate-400">
        <Info className="w-3 h-3" />
        <span>Based on {matched.length} identified switch opportunities totaling {fmt$(totalAnnualSavings)}/yr</span>
      </div>
    </div>
  );
}

// ─── Empty State ───────────────────────────────────────────
function EmptyState({ onDownloadTemplate }) {
  return (
    <div className="mt-8 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-8" data-testid="empty-state-demo">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-amber-500" />
        <h3 className="text-lg font-semibold text-slate-800">What your analyst can do</h3>
      </div>
      <p className="text-sm text-slate-500 mb-6">Upload your data — here's what a typical analysis reveals in under 30 seconds:</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 opacity-60">
        {[['$3.4M','Total Spend'], ['68%','Branded'], ['$480K','Savings'], ['6','Actions']].map(([v,l],i) => (
          <div key={i} className="bg-white border border-dashed border-slate-300 rounded-xl p-4 text-center">
            <p className={`text-2xl font-bold ${i===2?'text-emerald-400':'text-slate-400'}`}>{v}</p>
            <p className="text-xs text-slate-400 mt-1">{l}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-60">
        {[
          { t: 'Where You\'re Losing Money', v: '$420K', sub: 'In anticoagulants alone' },
          { t: 'Safest Place to Start', v: '3 switches', sub: 'High confidence, proven generics' },
          { t: 'Risks to Watch', v: '1 supply risk', sub: 'Action needed this month' },
          { t: '3 Actions This Month', v: '$350K', sub: 'Combined savings potential' },
        ].map((d, i) => (
          <div key={i} className="bg-white border border-dashed border-slate-300 rounded-lg p-4">
            <p className="text-sm font-semibold text-slate-500">{d.t}</p>
            <p className="text-lg font-bold text-slate-400 mt-1">{d.v}</p>
            <p className="text-xs text-slate-400">{d.sub}</p>
          </div>
        ))}
      </div>
      <button onClick={onDownloadTemplate} className="mt-6 btn-secondary flex items-center gap-2 text-sm" data-testid="download-template-btn">
        <Download className="w-4 h-4" /> Download sample template
      </button>
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [mode, setMode] = useState('cfo');
  const [guided, setGuided] = useState(true);
  const [wasteAlerts, setWasteAlerts] = useState([]);
  const [dailyLoss, setDailyLoss] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [uploadsRes, metricsRes, featuredRes, wasteRes, dailyLossRes] = await Promise.allSettled([
          getUploads(), getDashboardMetrics(), getFeaturedDrugs(), getWasteAlerts(5), getDailyLoss(),
        ]);
        if (wasteRes.status === 'fulfilled') setWasteAlerts(wasteRes.value.data.alerts || []);
        if (dailyLossRes.status === 'fulfilled') setDailyLoss(dailyLossRes.value.data);
        if (uploadsRes.status === 'fulfilled') {
          const uploads = uploadsRes.value.data.uploads || [];
          if (uploads.length > 0) {
            const latestId = uploads[0].id;
            setUploadId(latestId);
            const a = await getUploadAnalysis(latestId).catch(() => null);
            if (a?.data?.analysis) setAnalysis(a.data.analysis);
          }
        }
        if (metricsRes.status === 'fulfilled') setMetrics(metricsRes.value.data);
        if (featuredRes.status === 'fulfilled') setFeatured(featuredRes.value.data.drugs || []);
      } catch {}
    })();
  }, []);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await uploadFile(fd);
      setAnalysis(res.data.analysis);
      setUploadId(res.data.upload_id);
      toast.success(`Analyzed ${res.data.row_count} rows — insights ready`);
      // Refresh waste alerts + daily loss
      getWasteAlerts(5).then(r => setWasteAlerts(r.data.alerts || [])).catch(() => {});
      getDailyLoss().then(r => setDailyLoss(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to process file'); }
    finally { setUploading(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadSampleTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'HppyRx_Sample_Template.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const snapshot = analysis?.snapshot;
  const insights = analysis?.insights || [];
  const suggestions = analysis?.matched_drugs || [];
  const savingsEst = analysis?.savings_estimate;
  const totalLoss = insights.filter(i => i.type === 'savings_opportunity').reduce((s, i) => s + (i.impact_value || 0), 0);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            {snapshot ? 'Your Hospital Snapshot' : `Welcome, ${user?.name?.split(' ')[0] || 'there'}`}
          </h1>
          <p className="text-slate-500 text-sm">{snapshot ? 'Decision-ready intelligence from your data' : 'Upload data — your analyst starts working immediately'}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="mode-toggle">
            <button onClick={() => setMode('cfo')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${mode === 'cfo' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-slate-500'}`} data-testid="mode-cfo">CFO</button>
            <button onClick={() => setMode('pharmacy')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${mode === 'pharmacy' ? 'bg-white text-[#0A2540] shadow-sm' : 'text-slate-500'}`} data-testid="mode-pharmacy">Pharmacy</button>
          </div>
          <button onClick={() => setGuided(!guided)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${guided ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-slate-100 text-slate-500'}`} data-testid="guided-toggle">
            {guided ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />} Guided
          </button>
        </div>
      </div>

      {/* Upload */}
      <UploadZone onUpload={handleUpload} uploading={uploading} compact={!!snapshot} />
      <div className="flex items-center justify-between mt-1.5 mb-1">
        <p className="text-xs text-slate-400">Processed securely. Never shared.</p>
        <button onClick={handleDownloadTemplate} className="text-xs text-[#0A2540] hover:underline flex items-center gap-1" data-testid="download-template-link"><Download className="w-3 h-3" /> Template</button>
      </div>

      {snapshot ? (
        <div className="mt-5 space-y-5">
          {/* Next Best Action */}
          <NextBestAction suggestions={suggestions} guided={guided} />

          {/* Missed Yesterday — Daily Money Loss */}
          <MissedYesterday dailyLoss={dailyLoss} />

          {/* ROI Banner */}
          {savingsEst && savingsEst.high > 0 && (
            <div className="bg-gradient-to-r from-[#0A2540] to-[#1a3a5c] rounded-2xl p-5 text-white" data-testid="roi-banner">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                  <h2 className="font-bold">{mode === 'cfo' ? 'Your Savings Opportunity' : 'Substitution Opportunity'}</h2>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-emerald-400">{fmt$(savingsEst.low)} – {fmt$(savingsEst.high)}</p>
                  <p className="text-[10px] text-slate-400">Estimated annual savings</p>
                </div>
              </div>
              <div className="relative h-5 bg-red-500/20 rounded-full overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: `${Math.max(100 - (savingsEst.high / snapshot.total_spend * 100), 40)}%` }} />
                <div className="absolute inset-0 flex items-center justify-between px-3 text-[10px] font-bold text-white">
                  <span>Current: {fmt$(snapshot.total_spend)}</span>
                  <span>Optimized: {fmt$(snapshot.total_spend - savingsEst.high)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Snapshot KPIs */}
          <SnapshotRow snapshot={snapshot} savingsEst={savingsEst} mode={mode} />

          {/* 4-Card Decision Grid + ROI Calculator */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="decision-cards">
            <LosingMoneyCard insights={insights} totalLoss={totalLoss} />
            <SafestStartCard suggestions={suggestions} mode={mode} />
            <RisksCard insights={insights} wasteAlerts={wasteAlerts} />
            <ActionsThisMonthCard suggestions={suggestions} mode={mode} />
          </div>

          {/* ROI Calculator */}
          <RoiCalculator analysis={analysis} />

          {/* Waste Detection */}
          <WasteAlerts guided={guided} alerts={wasteAlerts} setAlerts={setWasteAlerts} />

          {/* Top Molecules */}
          {analysis?.top_molecules?.length > 0 && (
            <div>
              <h2 className="font-bold text-slate-900 mb-3">Top Molecules by Spend</h2>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="top-molecules-table">
                <table className="w-full">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    {['#','Drug','Spend','%','Distribution'].map(h => <th key={h} className={`text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-2.5 ${h==='Spend'||h==='%'?'text-right':'text-left'}`}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {analysis.top_molecules.slice(0, 7).map((m, i) => {
                      const pct = snapshot.total_spend > 0 ? (m.spend / snapshot.total_spend * 100) : 0;
                      return (
                        <tr key={i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                          <td className="px-4 py-2.5 text-sm text-slate-400">{i+1}</td>
                          <td className="px-4 py-2.5 text-sm font-medium text-slate-900">{m.name}</td>
                          <td className="px-4 py-2.5 text-sm text-right font-semibold">${m.spend?.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-sm text-right text-slate-600">{pct.toFixed(1)}%</td>
                          <td className="px-4 py-2.5"><div className="w-full bg-slate-100 rounded-full h-1.5"><div className="bg-[#0A2540] h-1.5 rounded-full" style={{ width: `${Math.min(pct * 2, 100)}%` }} /></div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <EmptyState onDownloadTemplate={handleDownloadTemplate} />
          {metrics && (
            <div className="mt-8">
              <h2 className="font-bold text-slate-900 mb-4">Intelligence Database</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                {[
                  { icon: Pill, label: 'Molecules', value: metrics.total_drugs || 50, color: 'bg-slate-700' },
                  { icon: CheckCircle, label: 'Generics', value: metrics.total_generics || 15, color: 'bg-emerald-600' },
                  { icon: TrendingUp, label: 'Opportunities', value: metrics.review_opportunities || 13, color: 'bg-amber-500' },
                  { icon: Target, label: 'High Confidence', value: metrics.high_confidence || 11, color: 'bg-blue-600' },
                ].map((k, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2"><div className={`w-8 h-8 rounded-lg flex items-center justify-center ${k.color}`}><k.icon className="w-4 h-4 text-white" /></div><span className="text-xs text-slate-500">{k.label}</span></div>
                    <p className="text-xl font-bold text-slate-900">{k.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {featured.slice(0, 6).map(drug => (
                  <Link key={drug.id} to={`/drug/${drug.id}`} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md group" data-testid={`featured-drug-${drug.id}`}>
                    <div className="flex items-center justify-between mb-1"><h3 className="font-semibold text-slate-900 group-hover:text-[#0A2540]">{drug.brand_name}</h3><ChevronRight className="w-4 h-4 text-slate-300" /></div>
                    <p className="text-sm text-slate-500">{drug.generic_name}</p>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded mt-1 inline-block">{drug.therapeutic_area}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2">
        <Shield className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500">Hppy Rx provides procurement intelligence. All substitution decisions require pharmacist and clinical governance approval.</p>
      </div>
    </div>
  );
}
